import type { OnSelectionChangeParams } from "@xyflow/react";
import { nanoid } from "nanoid";
import { memo, useCallback, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Card, type GridCard, LoadingSpinner, PanelOrientation, PanelPosition, ResizableTabPanels } from "widgets";

import { type WorkflowExtendedConfig } from "@/graphql.ts";
import { Fallback } from "@/modules/workflow/components/Fallback.tsx";
import { WorkflowPreview } from "@/modules/workflow/components/WorkflowPreview.tsx";
import { useGetActivitiesQuery } from "@/modules/workflow/graphql/workflow.gql.ts";
import type { Activity, PreviewNodeData } from "@/modules/workflow/utils/types.ts";
import { PreviewActivityInfoRenderer } from "@/ui-modules/entities/PreviewActivityInfoRenderer.tsx";
import {
  PreviewParameterRenderer,
  type PreviewParameterRendererProps,
} from "@/ui-modules/entities/PreviewParameterRenderer.tsx";

interface PlaybookEventWorkflowPreviewProps extends GridCard {
  workflowP2ExtendedConfig: WorkflowExtendedConfig | null;
  eventId: number;
}

export const PlaybookEventWorkflowPreviewCard = memo(function PlaybookEventWorkflowPreviewCard(
  props: PlaybookEventWorkflowPreviewProps,
) {
  // Show this card only for Processor events
  if (!props.workflowP2ExtendedConfig) {
    return null;
  }

  return (
    <ErrorBoundary fallbackRender={Fallback}>
      <Card title="Preview" data-testid="workflow-preview-card" noPadding fillHeight>
        <PreviewCardContent {...props} />
      </Card>
    </ErrorBoundary>
  );
});

const PreviewCardContent = (props: PlaybookEventWorkflowPreviewProps) => {
  const workflowConnectors = useMemo(
    () => props.workflowP2ExtendedConfig?.connectors ?? [],
    [props.workflowP2ExtendedConfig?.connectors],
  );
  const workflowActivities = useMemo(
    () => props.workflowP2ExtendedConfig?.activities ?? [],
    [props.workflowP2ExtendedConfig?.activities],
  );

  const { data, error, loading } = useGetActivitiesQuery();
  const processorActivities = data?.workflow?.activityCollector?.activities ?? [];

  if (error) {
    throw error;
  }

  // In case the query returns an empty list without error
  if (!loading && processorActivities == undefined) {
    throw Error("Failed to fetch activities");
  }

  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  // required for the tabs to render properly. As we can have the same activity
  // multiple times we need something unique
  const [tabUniqueKey, setTabUniqueKey] = useState<string>(nanoid());

  const handleSelect = useCallback((params: OnSelectionChangeParams) => {
    setSelectedActivity((params.nodes?.[0]?.data as PreviewNodeData)?.activity ?? null);
    setTabUniqueKey(nanoid());
  }, []);

  return loading ? (
    <LoadingSpinner inCenter />
  ) : (
    <ResizableTabPanels
      fixedPanel={PanelPosition.Trailing}
      orientation={PanelOrientation.Horizontal}
      defaultSize={250}
      minSize={240}
      maxSize={480}
      isContinuous
      data-testid="inspection-panel"
    >
      <ResizableTabPanels.FlexPanel>
        <WorkflowPreview
          workflowActivities={workflowActivities}
          workflowConnectors={workflowConnectors}
          processorActivities={processorActivities}
          onSelect={handleSelect}
          eventId={props.eventId}
        />
      </ResizableTabPanels.FlexPanel>
      <ResizableTabPanels.FixedPanel>
        <ResizableTabPanels.FixedPanelTab
          icon="settings_applications"
          title="Parameters"
          position={0}
          id={"parameters"}
          key={`preview-parameter-tab-${tabUniqueKey}`}
        >
          <PreviewParameterRenderer
            selectedActivity={
              selectedActivity
                ? workflowActivities.find(
                    (activity) =>
                      activity.name === selectedActivity.name && activity.version === selectedActivity.version.name,
                  )
                : undefined
            }
          />
        </ResizableTabPanels.FixedPanelTab>

        <ResizableTabPanels.FixedPanelTab
          icon="line_end_square"
          title="Activity"
          position={1}
          id={"activityInfo"}
          key={`preview-activity-info-tab-${tabUniqueKey}`}
        >
          <PreviewActivityInfoRenderer
            selectedActivity={
              selectedActivity
                ? ({
                    ...workflowActivities.find(
                      (a) => a.name === selectedActivity?.name && a.version === selectedActivity.version.name,
                    ),
                    label: selectedActivity?.label,
                  } as PreviewParameterRendererProps["selectedActivity"])
                : undefined
            }
            processorActivities={processorActivities}
          />
        </ResizableTabPanels.FixedPanelTab>
      </ResizableTabPanels.FixedPanel>
    </ResizableTabPanels>
  );
};
